<template>
  <q-layout view="hHr Lpr fFf">
    <q-header
      :style="{ height: '86px' }"
      class="row no-wrap bg-neutral-20 text-neutral-10"
      height-hint="86px"
    >
      <q-toolbar class="q-px-xl q-pb-xxs">
        <q-toolbar-title class="col-auto text-lg flex items-center">
          <div class="bg-neutral-20 flex items-center">
            <router-link class="flex" to="/home">
              <PoshnNucleusLogo
                :style="{
                  width: '122px',
                }"
              />
            </router-link>
          </div>
          <PBtn
            :class="[isLeftDrawerOpen ? 'toggled' : '']"
            :ripple="false"
            class="nav-toggle q-ml-2x-md"
            dense
            flat
            fluid
            @click="isLeftDrawerOpen = !isLeftDrawerOpen"
          >
            <ToggleNavbarIcon class="icon" />
          </PBtn>
          <ChatConnectionStateBadge v-if="heading === 'Chat'" />
        </q-toolbar-title>
        <GlobalSearchInput />
        <NotificationsPanel class="q-mr-2x-xl" />
        <div class="column justify-center items-end q-mx-xs">
          <p class="q-mb-none text-sm text-capitalize text-neutral">
            {{ user?.name || "Unknown" }}
          </p>
          <span class="text-xxs text-neutral-70">
            {{ user?.role_title || "Unknown" }}
          </span>
        </div>
        <q-avatar
          color="info-20"
          font-size="16px"
          size="32px"
          text-color="info-70"
        >
          <span class="text-uppercase">
            {{ userAvatarInitials }}
          </span>
        </q-avatar>
      </q-toolbar>
    </q-header>
    <q-drawer
      v-model="isLeftDrawerOpen"
      :width="232"
      class="bg-neutral-20"
      show-if-above
    >
      <q-list class="column" :style="{ height: '100%' }">
        <div class="col full-width overflow-y-scroll no-scrollbar q-mt-xl">
          <div class="overflow-hidden q-mr-xs">
            <ExpandableNavItem
              v-for="item in navItems"
              :key="item.title"
              :active="isActive(item)"
              :model-value="hasActive(item.subItems)"
              :disable="item.disable"
              :hide-expand-icon="!item.subItems"
              active-class="active"
              class="parent q-ml-xs"
              v-bind="item"
            >
              <ExpandableNavItem
                v-for="subItem in item.subItems"
                :key="subItem.title"
                :active="isActive(subItem)"
                :disable="subItem.disable"
                :header-inset-level="0.7"
                :hide-expand-icon="!subItem.subItems"
                active-class="active"
                v-bind="subItem"
              />
            </ExpandableNavItem>
          </div>
        </div>
        <q-separator spaced />
        <div class="flex justify-center q-px-md q-pb-md">
          <PBtn
            :loading="loggingOut"
            aria-label="Log Out"
            class="q-mx-md text-xs"
            color="error-70"
            dense
            flat
            @click="confirmLogout"
          >
            <SignOutIcon class="icon--sm q-mr-xs" />
            <span>Log Out</span>
          </PBtn>
          <p class="text-xxs q-mt-sm q-mb-none text-center">
            ©Poshn, Crafted with <span class="text-error-70">❤️</span> in India
          </p>
        </div>
      </q-list>
    </q-drawer>

    <q-drawer
      id="right-drawer"
      v-model="isRightDrawerOpen"
      behavior="desktop"
      class="bg-white overflow-hidden"
      side="right"
      overlay
      no-mini-animation
    >
      <component
        :is="rightDrawerComponent"
        v-bind="rightDrawerComponentProps"
      />
    </q-drawer>

    <q-drawer
      v-for="(drawer, index) in nestedRightDrawers"
      :id="`nested-right-drawer-${index}`"
      :key="index"
      v-model="drawer.open"
      behavior="desktop"
      class="bg-white overflow-hidden"
      side="right"
      overlay
      no-mini-animation
    >
      <component :is="drawer.component" v-bind="drawer.props" />
    </q-drawer>

    <q-page-container class="bg-neutral-20 relative-position">
      <router-view />
    </q-page-container>
  </q-layout>
</template>

<script>
export default {
  name: "DefaultLayout",
};
</script>

<script setup>
import { useMeta, useQuasar } from "quasar";

import PoshnNucleusLogo from "assets/poshn-nucleus.svg";

import HomeIcon from "assets/icons/home.svg";
import ChatIcon from "assets/icons/chat.svg";
import UsersIcon from "assets/icons/user.svg";
import RoleManagementIcon from "assets/icons/user-management.svg";
import DealIcon from "assets/icons/deals.svg";
import OrderIcon from "assets/icons/orders.svg";
import StockIcon from "assets/icons/stocks.svg";
import AccountsIcon from "assets/icons/profiles.svg";
import RFQIcon from "assets/icons/request-order.svg";
import SignOutIcon from "assets/icons/sign-out.svg";
import UploadDocumentIcon from "assets/icons/upload_document.svg";
import ToolsIcon from "assets/icons/star.svg";
import AnalyticsIcon from "assets/icons/analytics.svg";
import IntegrationsIcon from "assets/icons/integrations.svg";
import ShippingIcon from "assets/icons/shipping.svg";
import CashflowIcon from "assets/icons/cashflow.svg";
import ReportIcon from "assets/icons/reports.svg";
import ToggleNavbarIcon from "assets/icons/hamburger-menu-2.svg";

import PConfirmDialog from "src/components/PConfirmDialog.vue";
import {
  createPeriodicFetcher,
  getAvatarInitials,
} from "src/assets/scripts/utilities/helpers";
const heading = ref("");

const $q = useQuasar();

useMeta({
  titleTemplate: (title) => {
    heading.value = title;

    return `Poshn ${title ? "- " + title : ""}`;
  },
});

const { user } = storeToRefs(useAuthStore());

const { unreadConversationsCount } = storeToRefs(useChatStore());

const userAvatarInitials = computed(() => {
  return getAvatarInitials(unref(user)?.name);
});

const { logout } = useAuthStore();

const loggingOut = ref(false);

const onLogout = async () => {
  try {
    loggingOut.value = true;

    await logout();
  } catch (error) {
    console.error(error);
  } finally {
    loggingOut.value = false;
  }
};

function confirmLogout() {
  $q.dialog({
    component: PConfirmDialog,
    componentProps: {
      title: "Log Out",
      text: "Are you sure you want to Log Out ?",
      isFooterVisible: true,
      submitLabel: "Yes",
      submittable: true,
      cancelLabel: "No",
      cancellable: true,
      closable: true,
      onConfirm: onLogout,
    },
  });
}

const navItems = computed(() => [
  {
    icon: HomeIcon,
    route: { name: "dead" },
    title: "Home",
  },
  {
    disable: unref(heading) === "Chat",
    icon: UsersIcon,
    route: { name: "dead" },
    title: "Users",
    subItems: [
      {
        route: { name: "users", hash: "#customers" },
        title: "Customers",
      },
      {
        route: { name: "users", hash: "#vendors" },
        title: "Vendors",
      },
      {
        route: { name: "users", hash: "#team-members" },
        title: "Team Members",
      },
      {
        route: { name: "users-organisations" },
        title: "Organisations",
      },
    ],
  },
  {
    icon: RoleManagementIcon,
    route: { name: "roles-management" },
    title: "Roles Management",
  },
  {
    icon: StockIcon,
    route: { name: "dead" },
    title: "Catalogue",
    subItems: [
      {
        route: { name: "catalogue", hash: "#products" },
        title: "Products",
      },
      {
        route: { name: "catalogue", hash: "#categories" },
        title: "Categories",
      },
      {
        route: { name: "catalogue", hash: "#sub-categories" },
        title: "Sub Categories",
      },
      {
        route: { name: "catalogue", hash: "#brands" },
        title: "Brands",
      },
    ],
  },
  {
    disable: unref(heading) === "Chat",
    icon: DealIcon,
    route: { name: "dead" },
    title: "Trade",
    subItems: [
      {
        route: { name: "trade-rfqs" },
        title: "RFQs",
      },
      {
        route: { name: "trade-vendor-orders" },
        title: "Vendor Orders",
      },
    ],
  },
  {
    icon: RFQIcon,
    route: { name: "dead" },
    title: "Sales",
    subItems: [
      {
        route: { name: "sales-purchase-orders" },
        title: "Purchase Orders",
      },
      {
        route: { name: "sales-invoices" },
        title: "Invoices",
      },
      {
        route: { name: "sales-proof-of-deliveries" },
        title: "Proof of Deliveries",
      },
      {
        route: { name: "sales-credit-notes" },
        title: "Credit Notes",
      },
      {
        route: { name: "sales-debit-notes" },
        title: "Debit Notes",
      },
    ],
  },
  {
    icon: OrderIcon,
    route: { name: "dead" },
    title: "Purchases",
    subItems: [
      {
        route: { name: "purchases-purchase-bills" },
        title: "Purchase Bills",
      },
      {
        route: { name: "purchases-credit-notes" },
        title: "Credit Notes",
      },
      {
        route: { name: "purchases-debit-notes" },
        title: "Debit Notes",
      },
      {
        route: { name: "purchases-payments" },
        title: "Payments",
      },
    ],
  },
  {
    icon: ShippingIcon,
    route: { name: "dead" },
    title: "Shipping",
    subItems: [
      {
        route: { name: "shipping-e-way-bills" },
        title: "e-Waybills",
      },
    ],
  },
  {
    icon: AccountsIcon,
    title: "Accounts",
    route: { name: "dead" },
    subItems: [
      {
        route: { name: "accounts-invoice-requests" },
        title: "Invoice Requests",
      },
      {
        route: { name: "accounts-payment-requests" },
        title: "Payment Requests",
      },
      {
        route: { name: "accounts-receivables" },
        title: "Receivables",
      },
      {
        route: { name: "accounts-transactions" },
        title: "Transactions",
      },
    ],
  },
  {
    icon: ReportIcon,
    title: "Reports",
    route: { name: "dead" },
    subItems: [
      {
        route: {
          name: "reports-category",
          params: { category: "sales" },
        },
        title: "Sales",
      },
      {
        route: {
          name: "reports-category",
          params: { category: "purchases" },
        },
        title: "Purchases",
      },
      {
        route: {
          name: "reports-category",
          params: { category: "receivables" },
        },
        title: "Receivables",
      },
      {
        route: {
          name: "reports-category",
          params: { category: "payables" },
        },
        title: "Payables",
      },
      {
        route: {
          name: "ledgers-category",
          params: { category: "general-ledger" },
        },
        title: "General Ledger",
      },
      {
        route: {
          name: "ledgers-category",
          params: { category: "customer-ledger" },
        },
        title: "Customer Ledger",
      },
      {
        route: {
          name: "ledgers-category",
          params: { category: "vendor-ledger" },
        },
        title: "Vendor Ledger",
      },
    ],
  },
  {
    icon: ChatIcon,
    title: `Inbox (${unref(unreadConversationsCount) || 0})`,
    route: { name: "inbox" },
  },
  {
    icon: CashflowIcon,
    route: { name: "dead" },
    title: "Cashflow Manager",
    subItems: [
      {
        route: { name: "cashflow-manager-customer-payments" },
        title: "Customer Payments",
      },
      {
        route: { name: "cashflow-manager-vendor-payments" },
        title: "Vendor Payments",
      },
    ],
  },
  {
    icon: UploadDocumentIcon,
    route: { name: "file-manager" },
    title: "File Manager",
  },
  {
    icon: ToolsIcon,
    route: { name: "dead" },
    title: "Utility",
    subItems: [
      {
        route: { name: "utility-audit-logs" },
        title: "Audit Logs",
      },
      {
        route: { name: "utility-credit-score-check" },
        title: "Credit Score Check",
      },
      {
        href: "https://tools.poshn.app/p/gstr-analytics",
        title: "GSTR Analytics",
        target: "_blank",
      },
      {
        route: { name: "utility-ledger-requests" },
        title: "Request Ledger",
      },
      {
        route: { name: "utility-transaction-series" },
        title: "Transaction Series",
      },
      {
        route: { name: "utility-pdf-templates" },
        title: "PDF Templates",
      },
    ],
  },
  {
    icon: AnalyticsIcon,
    title: "Analytics",
    href: "https://bi.poshn.app/",
    target: "_blank",
  },
  {
    icon: IntegrationsIcon,
    route: { name: "dead" },
    title: "Integrations",
  },
]);

const isLeftDrawerOpen = ref(true);

const {
  isRightDrawerOpen,
  rightDrawerComponent,
  rightDrawerComponentProps,
  nestedRightDrawers,
} = storeToRefs(useAppStore());

const route = useRoute();

const isActive = (navItem) => {
  if (!navItem?.route?.name) return false;

  const routeMatch = route.name?.startsWith(navItem.route.name);

  const paramsMatch =
    !navItem.route?.params ||
    Object.entries(navItem.route.params).every(
      ([key, value]) => route.params[key] === value,
    );

  return routeMatch && paramsMatch && isActiveHash(navItem);
};

const isActiveHash = (navItem) => {
  if (!(route.hash || navItem?.route?.hash)) {
    return true;
  }

  return route.hash.includes(navItem?.route?.hash);
};

const hasActive = (subItems) => {
  return subItems?.some((subItem) => isActive(subItem));
};

const { getNotificationsCount } = useNotificationStore();

let fetchNotificationsCountIntervalId = null;

const startPeriodicFetching = async () => {
  fetchNotificationsCountIntervalId = createPeriodicFetcher(
    getNotificationsCount,
    { immediate: false },
  );
};

watch(
  () => route.fullPath,
  async () => {
    await nextTick();

    const activeItem = document.querySelector(".q-item.active");

    if (activeItem) {
      activeItem.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  },
  { immediate: true },
);

onBeforeMount(() => {
  startPeriodicFetching();
});

onBeforeUnmount(() => {
  clearInterval(fetchNotificationsCountIntervalId);
});
</script>

<style lang="scss" scoped>
.q-toolbar {
  &__title {
    min-width: 232px;
  }
}

:deep(.q-drawer) {
  background: transparent;

  [id*="right-drawer"] {
    border-radius: 8px 0 0 8px;
    box-shadow:
      0 0 10px 2px rgba(29, 43, 90, 0.2),
      0 0px 10px rgba(29, 43, 90, 0.24);
  }
}

.q-drawer-container {
  &:has([id*="right-drawer"]) {
    display: none;
    position: static;

    &:not(:has(.q-layout--prevent-focus)) {
      display: block;
      position: fixed;
    }

    top: 0;
    left: 0;
    right: 0;
    bottom: 0;

    background-color: rgba(0 0 0 / 0.4);
  }

  $drawer-z-index: 6500;

  &:has(#right-drawer) {
    z-index: $drawer-z-index;

    :deep(.q-drawer--right) {
      width: 50vw !important;
    }
  }

  @for $i from 0 through 3 {
    &:has([id="nested-right-drawer-#{$i}"]) {
      z-index: $drawer-z-index + $i;

      :deep(.q-drawer--right) {
        width: calc((4 - $i) * 10vw) !important;
      }
    }
  }
}
</style>
